import React from 'react';
import {
  DeviationEvent,
  useDeviations,
} from '../journey/deviations/fetchDeviations';
import './NewDeviationsView.scss';
import dayjs from 'dayjs';
import {
  formatDeviationEvent,
  formatDeviationMessage,
} from '../journey/deviations/DeviationList';
import Button, { ButtonGroup } from '../../../components/buttons/Button';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { postNewDeviation } from '../journey/deviations/DeviationForm';
import Fetch from '../../../utils/fetch';
import { settings } from '../../../settings';

type NewDeviationsViewProps = {
  orderId: string;
};

const NewDeviationsView = (props: NewDeviationsViewProps): JSX.Element => {
  const { deviations, refetchDeviations } = useDeviations(props.orderId);
  if (Object.keys(deviations).length === 0) return null;

  const senderDeviations: [string, DeviationEvent[]][] = [];
  const otherDeviations: [string, DeviationEvent[]][] = [];
  const courierDeviations: [string, DeviationEvent[]][] = [];
  let anyOpenDeviations = false;

  Object.entries(deviations).forEach(([key, deviations]) => {
    if (deviations[deviations.length - 1].deviationEventType !== 'CLOSE') {
      anyOpenDeviations = true;
    }
    switch (deviations[0].authorRole) {
      case 'COURIER':
        courierDeviations.push([key, deviations]);
        break;
      case 'SENDER':
        senderDeviations.push([key, deviations]);
        break;
      case 'SENDER_ADMIN':
        senderDeviations.push([key, deviations]);
        break;
      default:
        otherDeviations.push([key, deviations]);
    }
  });

  return (
    <div className={'deviationsView'}>
      {anyOpenDeviations && (
        <div className={'openDeviationsWarning'}>
          This order has open deviations
        </div>
      )}
      <h5>Deviation history</h5>
      <table>
        <thead>
          <tr>
            <th>Status </th>
            <th>Author</th>
            <th>Message</th>
            <th>Time</th>
            <th>Event type</th>
          </tr>
        </thead>
        <tbody>
          {senderDeviations.length > 0 && (
            <tr className={'separatorRow'}>
              <td colSpan={5}>Sender deviations</td>
            </tr>
          )}
          {senderDeviations.map(([key, deviations], i) => (
            <DeviationGroupRows
              key={key}
              deviations={deviations}
              evenRow={i % 2 === 0}
              orderId={props.orderId}
              refetch={refetchDeviations}
            />
          ))}
          {courierDeviations.length > 0 && (
            <tr className={'separatorRow'}>
              <td colSpan={5}>Courier deviations</td>
            </tr>
          )}
          {courierDeviations.map(([key, deviations], i) => (
            <DeviationGroupRows
              key={key}
              deviations={deviations}
              evenRow={i % 2 === 0}
              orderId={props.orderId}
              refetch={refetchDeviations}
            />
          ))}
          {otherDeviations.length > 0 && (
            <tr className={'separatorRow'}>
              <td colSpan={5}>Other deviations</td>
            </tr>
          )}
          {otherDeviations.map(([key, deviations], i) => (
            <DeviationGroupRows
              key={key}
              deviations={deviations}
              evenRow={i % 2 === 0}
              orderId={props.orderId}
              refetch={refetchDeviations}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DeviationGroupRows = ({
  deviations,
  evenRow,
  orderId,
  refetch,
}: {
  deviations: DeviationEvent[];
  evenRow: boolean;
  orderId: string;
  refetch: () => void;
}) => {
  const parent = deviations[0];
  const updates = deviations.slice(1);
  const closed =
    deviations[deviations.length - 1].deviationEventType === 'CLOSE';

  const submit = async (close: boolean, message: string) => {
    // TODO: Hack for auto closing old deviations
    if (parent.authorRole === 'COURIER' && close) {
      await closeOldDeviations(orderId, message);
    }

    await postNewDeviation(orderId, {
      deviationEventType: close ? 'CLOSE' : 'CHANGE',
      deviationMessage: message,
      deviationType: 'OTHER',
      deviationVisibilities: parent.deviationVisibilities,
      parentEventId: parent.id,
    });
    refetch();
  };

  const onUpdateDeviation = async (title: string) => {
    const message: SweetAlertResult<string> = await Swal.fire({
      input: 'textarea',
      title: title,
      inputPlaceholder: 'What has happened?',
      showCancelButton: true,
      confirmButtonText: 'Ok',
      cancelButtonText: 'Abort',
    });
    if (message.isConfirmed) {
      await submit(false, message.value);
    }
  };

  const onCloseDeviation = async () => {
    const message: SweetAlertResult<string> = await Swal.fire({
      input: 'textarea',
      title: 'Close deviation',
      text:
        'Resolving this deviation will also close any courier deviations in the old system. ' +
        'Note that the old deviation view needs to be reloaded manually.',
      inputPlaceholder: 'How was the deviation resolved?',
      showCancelButton: true,
      confirmButtonText: 'Ok',
      confirmButtonColor: '#72b46e',
      cancelButtonText: 'Abort',
    });
    if (message.isConfirmed) {
      await submit(true, message.value);
    }
  };

  return (
    <>
      <tr className={evenRow ? 'even parent' : 'odd parent'}>
        <td className={closed ? 'closed' : 'open'}>
          {closed ? 'Closed' : 'Open'}
        </td>
        <td>{parent.authorName}</td>
        <td>{formatDeviationMessage(parent)}</td>
        <td>{dayjs(parent.createdAt).format('DD.MM HH:mm')}</td>
        <td>{formatDeviationEvent(parent)}</td>
      </tr>
      {updates.map(deviation => (
        <tr key={deviation.id} className={evenRow ? 'even' : 'odd'}>
          <td />
          <td>{deviation.authorName}</td>
          <td>{formatDeviationMessage(deviation)}</td>
          <td>{dayjs(deviation.createdAt).format('DD.MM HH:mm')}</td>
          <td>{formatDeviationEvent(deviation)}</td>
        </tr>
      ))}
      <tr className={evenRow ? 'even toolRow' : 'odd toolRow'}>
        <td colSpan={5}>
          <div className={'toolContainer'}>
            {closed ? (
              <Button
                type={'warning'}
                title={'Reopen'}
                onClick={() => onUpdateDeviation('Reopen deviation')}
              />
            ) : (
              <ButtonGroup>
                <Button
                  title={'Update'}
                  onClick={() => onUpdateDeviation('Update deviation')}
                />
                <Button
                  type={'success'}
                  title={'Resolve'}
                  onClick={onCloseDeviation}
                />
              </ButtonGroup>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

async function closeOldDeviations(orderId: string, message: string) {
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/deviation/courier/${orderId}`,
    body: {
      deviationMessage: `Automatically closed by new deviation system. Message: ${message}`,
      deviationStatus: 'CLOSED',
    },
  });

  if (response.ok) {
    return;
  } else {
    throw Error('Failed to close old deviation');
  }
}

export default NewDeviationsView;
